import { Button, ContextualMessage, ProgressSpinner, T } from "@bbnpm/bb-ui-framework";
import { useEffect, useState } from "react";
import { Constants } from "../../launch/v2/launch-constants";
import { isManagedMac, MigrationPhases, Settings } from "../../settings";
import { checkClientVersion, VersionCheckState } from "./client-version-api";
import "./downloads.min.css";

const Client = () => {
    const SITE_ADDRESS = process.env.PUBLIC_URL + "/";
    const hasStartedMigration = Settings.migration.phase !== MigrationPhases.None;
    const managedMac = isManagedMac();
    const showParallelsDownloads = hasStartedMigration && !managedMac;
    const [selected, setSelected] = useState();

    const versionCheckEnabled =
        window.SETTINGS.clientVersionCheck?.enabled === true && window.SETTINGS.migrationPhase > 0;
    // When off, show the detected version without comparing it to the latest.
    const outdatedWarningEnabled =
        window.SETTINGS.clientVersionCheck?.outdatedWarningEnabled === true;
    const [versionStatus, setVersionStatus] = useState(null);
    const versionCheckBusy =
        versionStatus?.state === VersionCheckState.Starting ||
        versionStatus?.state === VersionCheckState.Polling;

    useEffect(() => {
        const links = document.querySelectorAll(".page-nav li");
        links.forEach((link) => link.classList.remove("selected"));
        selected && selected.classList.add("selected");
    }, [selected]);

    // subnav
    useEffect(() => {
        const mainContent = document.querySelector(".main-content");
        const links = document.querySelectorAll(".page-nav li");
        setSelected(links[0]);

        for (let i = 0; i < links.length; i++) {
            links[i].addEventListener("click", (e) => {
                const scrollToElm = document.getElementById(e.currentTarget.getAttribute("linkto"));

                mainContent.scrollTo({
                    top: scrollToElm.offsetTop - 60 - 10,
                    behavior: "smooth",
                });
            });
        }
    }, []);

    // intersection observer
    useEffect(() => {
        let scrollDirection = "down";
        let prevYPosition = 0;
        const links = document.querySelectorAll(".page-nav li");
        const header = document.querySelector("header.bbui-titlebar");
        const targets = document.querySelectorAll(".help-section-title");
        targets.forEach((target, idx) => (target["data-id"] = idx));

        const options = {
            root: document.querySelector("#root"),
            rootMargin: `${(header.offsetHeight + targets[0].offsetHeight * 2) * -1}px`,
            threshold: 0,
        };

        const setScrollDirection = () => {
            // .main-content is AppLayout.Main, the scroll container under kind="panel".
            const scrollRoot = document.querySelector(".main-content");
            scrollDirection = scrollRoot.scrollTop >= prevYPosition ? "down" : "up";
            prevYPosition = scrollRoot.scrollTop;
        };

        const handleIntersection = (entries) => {
            entries.forEach((entry) => {
                setScrollDirection();
                const sectionId = entry.target["data-id"];

                if (scrollDirection === "down") {
                    if (
                        (!entry.isIntersecting || sectionId === 0) &&
                        entry.boundingClientRect.top < 100
                    ) {
                        setSelected(links[sectionId]);
                    }
                } else {
                    if (entry.isIntersecting) {
                        links[sectionId].previousElementSibling &&
                            setSelected(links[sectionId].previousElementSibling);
                    }
                }
            });
        };

        const observer = new IntersectionObserver(handleIntersection, options);
        targets.forEach((target) => observer.observe(target));

        return () => observer.disconnect();
    }, []);

    return (
        <div className="client">
            <div className="help-body">
                <div className="page-nav">
                    <ul>
                        <li linkto="section_windows" className="selected">
                            Windows
                        </li>
                        <li linkto="section_mac">Mac</li>
                        <li linkto="section_linux">Linux</li>
                    </ul>
                </div>
                <div className="content">
                    <T.H1 className="page-title">BBA Downloads</T.H1>
                    {/* Temporary, will be removed for external release */}
                    {hasStartedMigration && (
                        <div className="need-help-box">
                            <T.BodyLarge as="div">
                                <T.H4 as="h2">Need Help?</T.H4>
                                <div>
                                    Visit {"{DRQS 183555622}"} or{" "}
                                    <a
                                        href="https://bloomberg.enterprise.slack.com/archives/C0AB330RXP1"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        #bba-support slack
                                    </a>{" "}
                                    for additional support.
                                </div>
                                <dl className="contact-list">
                                    <dt>The Americas</dt>
                                    <dd>+1 212 318 2000</dd>
                                    <dt>Europe, Middle East, &amp; Africa</dt>
                                    <dd>+44 20 7330 7500</dd>
                                    <dt>Asia Pacific</dt>
                                    <dd>+65 6212 1000</dd>
                                </dl>
                            </T.BodyLarge>
                            <T.BodyLarge as="div" className="parallels-note">
                                <T.H4 as="h2">Having trouble with Parallels?</T.H4>
                                <div>
                                    The Citrix option will be temporarily available as a fallback
                                    for users who are experiencing critical issues with Parallels
                                    RAS. To access Citrix, use the dropdown arrow next to “Launch”
                                    in BBA.
                                    {/* The TEAM page is internal, so only show on internal cluster*/}
                                    {Settings.ui.isCorp && (
                                        <>
                                            {" "}
                                            For additional help and support. Please visit:{" "}
                                            <a
                                                href={Constants.MigrationFaqUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                TEAMS &gt;
                                            </a>
                                        </>
                                    )}
                                </div>
                                {versionCheckEnabled && (
                                    <div className="client-version-check">
                                        <Button
                                            kind="secondary"
                                            onClick={() => checkClientVersion(setVersionStatus)}
                                            disabled={versionCheckBusy}
                                        >
                                            Check My Client Version
                                        </Button>
                                        {versionCheckBusy && (
                                            <span className="client-version-status">
                                                <ProgressSpinner size="small" />
                                                Checking for your Parallels Client...
                                            </span>
                                        )}
                                        {versionStatus?.state === VersionCheckState.Received &&
                                            !outdatedWarningEnabled && (
                                                <span className="client-version-status client-version-ok">
                                                    Your Parallels Client is{" "}
                                                    {versionStatus.version
                                                        ? `version ${versionStatus.version}`
                                                        : "installed"}
                                                    .
                                                </span>
                                            )}
                                        {versionStatus?.state === VersionCheckState.Received &&
                                            outdatedWarningEnabled &&
                                            !versionStatus.outdated && (
                                                <span className="client-version-status client-version-ok">
                                                    Your Parallels Client
                                                    {versionStatus.version
                                                        ? ` (version ${versionStatus.version})`
                                                        : ""}{" "}
                                                    is up to date.
                                                </span>
                                            )}
                                        {versionStatus?.state === VersionCheckState.Received &&
                                            outdatedWarningEnabled &&
                                            versionStatus.outdated && (
                                                <span className="client-version-status client-version-warning">
                                                    Your Parallels Client
                                                    {versionStatus.version
                                                        ? ` (version ${versionStatus.version})`
                                                        : ""}{" "}
                                                    is outdated
                                                    {versionStatus.latestVersion
                                                        ? `; the latest version is ${versionStatus.latestVersion}`
                                                        : ""}
                                                    . Please download the latest client below.
                                                </span>
                                            )}
                                        {versionStatus?.state ===
                                            VersionCheckState.VersionUnknown && (
                                            <span className="client-version-status client-version-warning">
                                                We found a Parallels Client but couldn't detect its
                                                version number, so we can't tell whether it is up to
                                                date. You can download the latest client below.
                                            </span>
                                        )}
                                        {versionStatus?.state === VersionCheckState.NotDetected && (
                                            <span className="client-version-status client-version-warning">
                                                We couldn't detect a Parallels Client. If it isn't
                                                installed, you can download it below.
                                            </span>
                                        )}
                                        {versionStatus?.state === VersionCheckState.Error && (
                                            <span className="client-version-status client-version-warning">
                                                Something went wrong checking your client version.
                                                Please try again.
                                            </span>
                                        )}
                                    </div>
                                )}
                            </T.BodyLarge>
                        </div>
                    )}
                    {/* Managed Macs get the client pushed to them, so the download
                        steps below do not apply: they re-install through Self Service. */}
                    {managedMac && (
                        <div className="need-help-box reinstall-box">
                            <T.BodyLarge as="div">
                                <T.H4 as="h2">If you would like to re-install</T.H4>
                                <div>
                                    Open the Bloomberg Self Service application and re-install the
                                    “Reinstall Parallels Client” application.{" "}
                                    <a
                                        className="reinstall-help-link"
                                        href={Constants.MigrationFaqUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Learn more here &gt;
                                    </a>
                                </div>
                                {/* Wrapped so the spacing lives on our element rather
                                    than on the BBUI component's own class. */}
                                <div className="reinstall-warning">
                                    <ContextualMessage kind="warning">
                                        Please DO NOT install the client using any other method,
                                        this could result in the client crashing.
                                    </ContextualMessage>
                                </div>
                            </T.BodyLarge>
                        </div>
                    )}
                    <div id="section_windows" className="help-section">
                        <T.H2 as="div" className="help-section-title">
                            Windows
                        </T.H2>

                        {showParallelsDownloads &&
                            window.SETTINGS.clientDownloads.parallels?.windows && (
                                <>
                                    <div className="help-section-content contact-section">
                                        <T.H3 as="h1">Parallels Client</T.H3>
                                        <T.H4 as="h2">Installation</T.H4>
                                        <div className="sub-section-content">
                                            <T.BodyLarge as="span">
                                                Download Parallels Client{" "}
                                                {
                                                    window.SETTINGS.clientDownloads.parallels
                                                        .windows.version
                                                }{" "}
                                                <a
                                                    href={
                                                        SITE_ADDRESS + "downloads/parallels/windows"
                                                    }
                                                >
                                                    here
                                                </a>
                                            </T.BodyLarge>
                                            <br />
                                            {window.SETTINGS.clientDownloads.parallels.windows
                                                .url && (
                                                <T.BodyLarge as="span">
                                                    Alternately, you can download the client from{" "}
                                                    <a
                                                        href={
                                                            window.SETTINGS.clientDownloads
                                                                .parallels.windows.url
                                                        }
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        Parallels
                                                    </a>
                                                </T.BodyLarge>
                                            )}
                                        </div>
                                        <div className="step-row">
                                            <div className="step-card">
                                                <T.H3 as="div" className="step-number">
                                                    01
                                                </T.H3>
                                                <T.Body as="div" className="step-desc">
                                                    Click 'Keep/Save' when prompted by the download.
                                                </T.Body>
                                            </div>
                                            <div className="step-card">
                                                <T.H3 as="div" className="step-number">
                                                    02
                                                </T.H3>
                                                <T.Body as="div" className="step-desc">
                                                    After download is complete, click on the file to
                                                    begin installation.
                                                </T.Body>
                                            </div>
                                            <div className="step-card">
                                                <T.H3 as="div" className="step-number">
                                                    03
                                                </T.H3>
                                                <T.Body as="div" className="step-desc">
                                                    Once completed, go to the{" "}
                                                    <a href="/">Bloomberg Anywhere login page.</a>
                                                </T.Body>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="section" id="help-link-parent">
                                        <T.BodyLarge
                                            as="a"
                                            className="help-link"
                                            href="/help#section_windows"
                                            target="_blank"
                                        >
                                            Need more help?
                                        </T.BodyLarge>
                                    </div>
                                    <br></br>
                                    <hr />
                                    <br></br>
                                </>
                            )}
                        {window.SETTINGS.clientDownloads.citrix?.windows && (
                            <>
                                <div className="help-section-content contact-section">
                                    <T.H3 as="h1">Citrix Workspace App</T.H3>
                                    <T.H4 as="h2">Installation</T.H4>
                                    <div className="sub-section-content">
                                        <T.BodyLarge as="span">
                                            Download Citrix Workspace App{" "}
                                            {window.SETTINGS.clientDownloads.citrix.windows.version}{" "}
                                            <a href={SITE_ADDRESS + "downloads/citrix/windows"}>
                                                here
                                            </a>
                                        </T.BodyLarge>
                                        <br />
                                        {window.SETTINGS.clientDownloads.citrix.windows.url && (
                                            <T.BodyLarge as="span">
                                                Alternately, you can download the client from{" "}
                                                <a
                                                    href={
                                                        window.SETTINGS.clientDownloads.citrix
                                                            .windows.url
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Citrix
                                                </a>
                                            </T.BodyLarge>
                                        )}
                                    </div>
                                    <div className="step-row">
                                        <div className="step-card">
                                            <T.H3 as="div" className="step-number">
                                                01
                                            </T.H3>
                                            <T.Body as="div" className="step-desc">
                                                Click 'Keep/Save' when prompted by the download.
                                            </T.Body>
                                        </div>
                                        <div className="step-card">
                                            <T.H3 as="div" className="step-number">
                                                02
                                            </T.H3>
                                            <T.Body as="div" className="step-desc">
                                                After download is complete, click on the file to
                                                begin installation.
                                            </T.Body>
                                        </div>
                                        <div className="step-card">
                                            <T.H3 as="div" className="step-number">
                                                03
                                            </T.H3>
                                            <T.Body as="div" className="step-desc">
                                                After plugin installation, restart your browser.
                                            </T.Body>
                                        </div>
                                        <div className="step-card">
                                            <T.H3 as="div" className="step-number">
                                                04
                                            </T.H3>
                                            <T.Body as="div" className="step-desc">
                                                Once completed, go to the{" "}
                                                <a href="/">Bloomberg Anywhere login page.</a>
                                            </T.Body>
                                        </div>
                                    </div>
                                </div>
                                <div className="section" id="help-link-parent">
                                    <T.BodyLarge
                                        as="a"
                                        className="help-link"
                                        href="/help#section_windows"
                                        target="_blank"
                                    >
                                        Need more help?
                                    </T.BodyLarge>
                                </div>
                            </>
                        )}
                    </div>
                    <div id="section_mac" className="help-section">
                        <T.H2 as="div" className="help-section-title">
                            Mac
                        </T.H2>
                        {showParallelsDownloads &&
                            window.SETTINGS.clientDownloads.parallels?.mac && (
                                <>
                                    <div className="help-section-content contact-section">
                                        <T.H3 as="h1">Parallels Client</T.H3>
                                        <T.H4 as="h2">Installation</T.H4>
                                        <div className="sub-section-content">
                                            <T.BodyLarge as="span">
                                                Download Parallels Client{" "}
                                                {
                                                    window.SETTINGS.clientDownloads.parallels.mac
                                                        .version
                                                }{" "}
                                                <a href={SITE_ADDRESS + "downloads/parallels/mac"}>
                                                    here
                                                </a>
                                            </T.BodyLarge>
                                            <br />
                                            {window.SETTINGS.clientDownloads.parallels.mac.url && (
                                                <T.BodyLarge as="span">
                                                    Alternately, you can download the client from{" "}
                                                    <a
                                                        href={
                                                            window.SETTINGS.clientDownloads
                                                                .parallels.mac.url
                                                        }
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        Parallels
                                                    </a>
                                                </T.BodyLarge>
                                            )}
                                        </div>
                                        <div className="step-row">
                                            <div className="step-card">
                                                <T.H3 as="div" className="step-number">
                                                    01
                                                </T.H3>
                                                <T.Body as="div" className="step-desc">
                                                    Click 'Keep/Save' when prompted by the download.
                                                </T.Body>
                                            </div>
                                            <div className="step-card">
                                                <T.H3 as="div" className="step-number">
                                                    02
                                                </T.H3>
                                                <T.Body as="div" className="step-desc">
                                                    After download is complete, click on the file to
                                                    begin installation.
                                                </T.Body>
                                            </div>
                                            <div className="step-card">
                                                <T.H3 as="div" className="step-number">
                                                    03
                                                </T.H3>
                                                <T.Body as="div" className="step-desc">
                                                    Once completed, go to the{" "}
                                                    <a href="/">Bloomberg Anywhere login page.</a>
                                                </T.Body>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="section" id="help-link-parent">
                                        <T.BodyLarge
                                            as="a"
                                            className="help-link"
                                            href="/help#section_windows"
                                            target="_blank"
                                        >
                                            Need more help?
                                        </T.BodyLarge>
                                    </div>
                                    <br></br>
                                    <hr />
                                </>
                            )}
                        {window.SETTINGS.clientDownloads.citrix?.mac && (
                            <>
                                <div className="help-section-content contact-section">
                                    <T.H3 as="h1">Citrix Workspace App</T.H3>
                                    <T.H4 as="h2">Installation</T.H4>
                                    <div className="sub-section-content">
                                        <T.BodyLarge as="span">
                                            Download Citrix Workspace App{" "}
                                            {window.SETTINGS.clientDownloads.citrix.mac.version}{" "}
                                            <a href={SITE_ADDRESS + "downloads/citrix/mac"}>here</a>
                                        </T.BodyLarge>
                                        <br />
                                        {window.SETTINGS.clientDownloads.citrix.mac.url && (
                                            <T.BodyLarge as="span">
                                                Alternately, you can download the client from{" "}
                                                <a
                                                    href={
                                                        window.SETTINGS.clientDownloads.citrix.mac
                                                            .url
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Citrix
                                                </a>
                                            </T.BodyLarge>
                                        )}
                                    </div>
                                    <div className="step-row">
                                        <div className="step-card">
                                            <T.H3 as="div" className="step-number">
                                                01
                                            </T.H3>
                                            <T.Body as="div" className="step-desc">
                                                Once the file has downloaded, close all web
                                                browsers.
                                            </T.Body>
                                        </div>
                                        <div className="step-card">
                                            <T.H3 as="div" className="step-number">
                                                02
                                            </T.H3>
                                            <T.Body as="div" className="step-desc">
                                                Launch "CitrixWorkspaceApp.dmg"
                                            </T.Body>
                                        </div>
                                        <div className="step-card">
                                            <T.H3 as="div" className="step-number">
                                                03
                                            </T.H3>
                                            <T.Body as="div" className="step-desc">
                                                If you have an existing version choose "Uninstall
                                                Citrix Receiver", otherwise, choose the install
                                                option and follow the prompts.
                                            </T.Body>
                                        </div>
                                        <div className="step-card">
                                            <T.H3 as="div" className="step-number">
                                                04
                                            </T.H3>
                                            <T.Body as="div" className="step-desc">
                                                Once completed, go to the{" "}
                                                <a href="/">Bloomberg Anywhere login page.</a>
                                            </T.Body>
                                        </div>
                                    </div>
                                </div>
                                <div className="section" id="help-link-parent">
                                    <T.BodyLarge
                                        as="a"
                                        className="help-link"
                                        href="/help#section_mac"
                                        target="_blank"
                                    >
                                        Need more help?
                                    </T.BodyLarge>
                                </div>
                            </>
                        )}
                    </div>
                    <div id="section_linux" className="help-section">
                        <T.H2 as="div" className="help-section-title">
                            Linux
                        </T.H2>
                        {window.SETTINGS.clientDownloads.citrix?.linux && (
                            <>
                                <div className="help-section-content contact-section">
                                    <T.H3 as="h1">Citrix Workspace App</T.H3>
                                    <T.H4 as="h2">Installation</T.H4>
                                    <div className="sub-section-content">
                                        {window.SETTINGS.clientDownloads.citrix.linux.url && (
                                            <T.BodyLarge as="span">
                                                Choose a client below, or visit{" "}
                                                <a
                                                    href={
                                                        window.SETTINGS.clientDownloads.citrix.linux
                                                            .url
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Citrix
                                                </a>{" "}
                                                for more downloads.
                                            </T.BodyLarge>
                                        )}
                                    </div>
                                    <div className="sub-section-content">
                                        <div className="install_step_title">
                                            <T.BodyXLargeBold as="span">
                                                For x86 Systems
                                            </T.BodyXLargeBold>
                                        </div>
                                        <div>
                                            <div>
                                                <T.BodyLarge
                                                    as="a"
                                                    href={
                                                        SITE_ADDRESS +
                                                        "downloads/citrix/linux_rpm_x86"
                                                    }
                                                >
                                                    Receiver for Linux (.rpm)
                                                </T.BodyLarge>
                                            </div>
                                            <div>
                                                <T.BodyLarge
                                                    as="a"
                                                    href={
                                                        SITE_ADDRESS +
                                                        "downloads/citrix/linux_deb_x86"
                                                    }
                                                >
                                                    Receiver for Linux (.deb)
                                                </T.BodyLarge>
                                            </div>
                                            <div>
                                                <T.BodyLarge
                                                    as="a"
                                                    href={
                                                        SITE_ADDRESS +
                                                        "downloads/citrix/linux_tar_x86"
                                                    }
                                                >
                                                    Receiver for Linux (.tar.gz)
                                                </T.BodyLarge>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="sub-section-content">
                                        <div className="install_step_title">
                                            <T.BodyXLargeBold as="span">
                                                For 64-bit Systems
                                            </T.BodyXLargeBold>
                                        </div>
                                        <div>
                                            <div>
                                                <T.BodyLarge
                                                    as="a"
                                                    href={
                                                        SITE_ADDRESS +
                                                        "downloads/citrix/linux_rpm_64"
                                                    }
                                                >
                                                    Receiver for Linux (.rpm)
                                                </T.BodyLarge>
                                            </div>
                                            <div>
                                                <T.BodyLarge
                                                    as="a"
                                                    href={
                                                        SITE_ADDRESS +
                                                        "downloads/citrix/linux_deb_64"
                                                    }
                                                >
                                                    Receiver for Linux (.deb)
                                                </T.BodyLarge>
                                            </div>
                                            <div>
                                                <T.BodyLarge
                                                    as="a"
                                                    href={
                                                        SITE_ADDRESS +
                                                        "downloads/citrix/linux_tar_64"
                                                    }
                                                >
                                                    Receiver for Linux (.tar.gz)
                                                </T.BodyLarge>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="sub-section-content">
                                        <div className="install_step_title">
                                            <T.BodySmall as="span">
                                                * All clients require OpenMotif v.2.3.1
                                            </T.BodySmall>
                                        </div>
                                    </div>
                                </div>
                                <div className="section" id="help-link-parent">
                                    <T.BodyLarge
                                        as="a"
                                        className="help-link"
                                        href="/help#section_linux"
                                        target="_blank"
                                    >
                                        Need more help?
                                    </T.BodyLarge>
                                </div>
                            </>
                        )}
                        <br />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Client;
